/**
 * Centralized query key factory for React Query.
 * This provides type-safe query keys and enables better cache invalidation.
 *
 * KEY DESIGN PRINCIPLE: All keys within a domain share a common prefix.
 * This allows `invalidateQueries({ queryKey: ["fields"] })` to invalidate
 * ALL field-related queries (list, detail, byFarm, etc).
 *
 * Pattern: ["domain", "subtype", ...params]
 * Example: ["fields", "detail", "field-123"]
 */
export const queryKeys = {
  // User
  user: {
    all: ["users"] as const,
    detail: (userId: string) => ["users", "detail", userId] as const,
  },

  // Farms
  farms: {
    all: ["farms"] as const,
    detail: (farmId: string) => ["farms", "detail", farmId] as const,
  },

  // Fields
  fields: {
    all: ["fields"] as const,
    byFarm: (farmId: string) => ["fields", "byFarm", farmId] as const,
    detail: (fieldId: string) => ["fields", "detail", fieldId] as const,
  },

  // Arable Fields
  arableFields: {
    all: ["arableFields"] as const,
    ids: (farmId: string) => ["arableFields", "ids", farmId] as const,
    byFarm: (farmId: string) => ["arableFields", "byFarm", farmId] as const,
    detail: (fieldId: string) => ["arableFields", "detail", fieldId] as const,
  },

  // Animal Groups
  animalGroups: {
    all: ["animalGroups"] as const,
    byFarm: (farmId: string) => ["animalGroups", "byFarm", farmId] as const,
    detail: (animalGroupId: string) =>
      ["animalGroups", "detail", animalGroupId] as const,
    moveEvents: (animalGroupId: string) =>
      ["animalGroups", "moveEvents", animalGroupId] as const,
  },

  // Stock Classes
  stockClasses: {
    all: ["stockClasses"] as const,
    byAnimalGroup: (animalGroupId: string) =>
      ["stockClasses", "byAnimalGroup", animalGroupId] as const,
    detail: (stockClassId: string) =>
      ["stockClasses", "detail", stockClassId] as const,
  },

  // Feed Stock
  feedStock: {
    all: ["feedStock"] as const,
    byFarm: (farmId: string) => ["feedStock", "byFarm", farmId] as const,
  },

  // Stock Class Feed Allocations
  feedAllocations: {
    all: ["feedAllocations"] as const,
    byStockClass: (stockClassId: string) =>
      ["feedAllocations", "byStockClass", stockClassId] as const,
  },

  // Stock Class Birthing Periods
  birthingPeriods: {
    all: ["birthingPeriods"] as const,
    byStockClass: (stockClassId: string) =>
      ["birthingPeriods", "byStockClass", stockClassId] as const,
  },

  // Default Stock Classes
  defaultStockClasses: {
    all: ["defaultStockClasses"] as const,
    demandTemplates: ["defaultStockClasses", "demandTemplates"] as const,
  },

  // Events
  events: {
    readings: {
      all: ["readings"] as const,
      byField: (fieldId: string) => ["readings", "byField", fieldId] as const,
      byFarm: (farmId: string) => ["readings", "byFarm", farmId] as const,
    },
    yield: {
      all: ["yieldEvents"] as const,
      byField: (fieldId: string) =>
        ["yieldEvents", "byField", fieldId] as const,
      byFarm: (farmId: string) => ["yieldEvents", "byFarm", farmId] as const,
    },
    feed: {
      all: ["feedEvents"] as const,
      byField: (fieldId: string) => ["feedEvents", "byField", fieldId] as const,
      byFarm: (farmId: string) => ["feedEvents", "byFarm", farmId] as const,
    },
    feedPurchase: {
      all: ["feedPurchaseEvents"] as const,
      byFarm: (farmId: string) =>
        ["feedPurchaseEvents", "byFarm", farmId] as const,
    },
    fertilizer: {
      all: ["fertilizerEvents"] as const,
      byField: (fieldId: string) =>
        ["fertilizerEvents", "byField", fieldId] as const,
      byFarm: (farmId: string) =>
        ["fertilizerEvents", "byFarm", farmId] as const,
    },
    reseed: {
      all: ["reseedEvents"] as const,
      byField: (fieldId: string) =>
        ["reseedEvents", "byField", fieldId] as const,
      byFarm: (farmId: string) => ["reseedEvents", "byFarm", farmId] as const,
    },
    fieldPreparation: {
      all: ["fieldPreparationEvents"] as const,
      byField: (fieldId: string) =>
        ["fieldPreparationEvents", "byField", fieldId] as const,
      byFarm: (farmId: string) =>
        ["fieldPreparationEvents", "byFarm", farmId] as const,
    },
    diary: {
      all: ["diaryEntries"] as const,
      byFarm: (farmId: string) => ["diaryEntries", "byFarm", farmId] as const,
      byField: (fieldId: string) =>
        ["diaryEntries", "byField", fieldId] as const,
      detail: (diaryEntryId: string) =>
        ["diaryEntries", "detail", diaryEntryId] as const,
    },
  },

  // Tiles
  tiles: {
    all: ["tiles"] as const,
    byField: (fieldId: string) => ["tiles", "byField", fieldId] as const,
    detail: (tileId: string) => ["tiles", "detail", tileId] as const,
  },

  // Dashboards
  dashboards: {
    all: ["dashboards"] as const,
    farmer: ["dashboards", "farmer"] as const,
    project: ["dashboards", "project"] as const,
    accessibleFarmer: ["dashboards", "accessibleFarmer"] as const,
    accessibleFarmerProgrammes: [
      "dashboards",
      "accessibleFarmerProgrammes",
    ] as const,
    metabaseUrl: (dashboardId: string) =>
      ["dashboards", "metabaseUrl", dashboardId] as const,
  },

  // Programme Actions
  programmeActions: {
    all: ["programmeActions"] as const,
    submitted: (email: string) =>
      ["programmeActions", "submitted", email] as const,
  },

  // Quick Assessment
  quickAssessment: {
    all: ["quickAssessment"] as const,
    results: ["quickAssessment", "results"] as const,
  },

  // File Entries
  fileEntries: {
    all: ["fileEntries"] as const,
    byUser: (userId: string) => ["fileEntries", "byUser", userId] as const,
    detail: (fileEntryId: string) =>
      ["fileEntries", "detail", fileEntryId] as const,
  },

  // Farmer Image Uploads
  farmerImageUploads: {
    all: ["farmerImageUploads"] as const,
    detail: (imageUploadId: string) =>
      ["farmerImageUploads", "detail", imageUploadId] as const,
  },

  // Users with File Access
  usersWithFileAccess: {
    all: ["usersWithFileAccess"] as const,
  },

  // Accessible Users
  accessibleUsers: {
    all: ["accessibleUsers"] as const,
    detail: (userId: string) => ["accessibleUsers", "detail", userId] as const,
  },

  // Insights (Cube.js analytics)
  insights: {
    all: ["insights"] as const,
    meta: ["insights", "meta"] as const,
  },
};
